import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Package, ShoppingCart, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import RunningBanner from "../components/RunningBanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Hunter({ user }) {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [protocol, setProtocol] = useState([]);
  const [packages, setPackages] = useState(1);
  const [newItem, setNewItem] = useState({ name: "", price: 0, stock: 0 });
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: "" });
  const [editMode, setEditMode] = useState(false); // Bearbeitungsmodus für hunter_editor
  
  // Stock Dialog States
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [stockItems, setStockItems] = useState([]); // Items to add/remove
  const [selectedItemId, setSelectedItemId] = useState("");
  const [stockQuantity, setStockQuantity] = useState("");
  const [stockPersonalUse, setStockPersonalUse] = useState(false);
  
  // Check if user has hunter_editor permission
  const isHunterEditor = user.permissions?.includes("hunter_editor") || false;

  useEffect(() => {
    loadInventory();
    loadProtocol();
    
    // Auto-refresh every 10 seconds for live updates
    const intervalId = setInterval(() => {
      loadInventory();
      loadProtocol();
    }, 10000); // 10 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  const loadInventory = async () => {
    try {
      const res = await axios.get(`${API}/inventory`);
      setInventory(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Inventars");
    }
  };

  const loadProtocol = async () => {
    try {
      const res = await axios.get(`${API}/protocol`);
      setProtocol(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Protokolls");
    }
  };

  // Group protocol entries - orders and batches are grouped together
  const getGroupedProtocol = () => {
    const grouped = [];
    const processedOrderIds = new Set();
    const processedBatchIds = new Set();
    
    protocol.forEach(log => {
      if (log.is_order && log.order_id && !processedOrderIds.has(log.order_id)) {
        // This is an order - find all items with same order_id
        const orderItems = protocol.filter(l => l.order_id === log.order_id);
        grouped.push({
          type: 'order',
          id: log.order_id,
          order_id: log.order_id,
          items: orderItems,
          display_name: log.display_name || log.username,
          order_customer: log.order_customer,
          timestamp: log.timestamp,
          user_id: log.user_id
        });
        processedOrderIds.add(log.order_id);
      } else if (log.is_batch && log.batch_id && !processedBatchIds.has(log.batch_id)) {
        // This is a batch operation - find all items with same batch_id
        const batchItems = protocol.filter(l => l.batch_id === log.batch_id);
        grouped.push({
          type: 'batch',
          id: log.batch_id,
          batch_id: log.batch_id,
          items: batchItems,
          display_name: log.display_name || log.username,
          timestamp: log.timestamp,
          user_id: log.user_id
        });
        processedBatchIds.add(log.batch_id);
      } else if (!log.is_order && !log.is_batch) {
        // Regular log entry
        grouped.push({
          type: 'single',
          ...log
        });
      }
    });
    
    return grouped;
  };

  const calculatePackagePrice = () => {
    const qty = parseInt(packages) || 0;
    let pricePerPackage = 40;
    if (qty >= 10) pricePerPackage = 35;
    else if (qty >= 5) pricePerPackage = 37;
    return qty * pricePerPackage;
  };

  const calculateCrafting = () => {
    const qty = parseInt(packages) || 0;
    const bratwurstNeeded = qty * 50;
    const dosenwurstNeeded = qty * 50;
    
    // Bratwurst: 1 Tierdarm + 1 Schweinefleisch = 15 Bratwurst
    const bratwurstCrafts = Math.ceil(bratwurstNeeded / 15);
    const tierdarm = bratwurstCrafts;
    const schweinefleisch = bratwurstCrafts;
    
    // Dosenwurst: 1 Dose + 2 Fett + 5 Wildfleisch = 10 Dosenwurst
    const dosenwurstCrafts = Math.ceil(dosenwurstNeeded / 10);
    const dose = dosenwurstCrafts;
    const fett = dosenwurstCrafts * 2;
    const wildfleisch = dosenwurstCrafts * 5;
    
    return {
      bratwurst: bratwurstNeeded,
      dosenwurst: dosenwurstNeeded,
      materials: {
        bratwurstCrafts,
        dosenwurstCrafts,
        tierdarm,
        schweinefleisch,
        dose,
        fett,
        wildfleisch
      }
    };
  };

  const handleAddItem = async () => {
    try {
      await axios.post(`${API}/inventory`, newItem);
      toast.success("Item hinzugefügt");
      setNewItem({ name: "", price: 0, stock: 0 });
      setShowAddForm(false);
      loadInventory();
    } catch (err) {
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleUpdateItem = async () => {
    try {
      await axios.put(`${API}/inventory/${editingItem.id}`, editingItem);
      toast.success("Item aktualisiert");
      setEditingItem(null);
      loadInventory();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteItem = async (id) => {
    if (!isHunterEditor) {
      toast.error("Keine Berechtigung zum Löschen");
      return;
    }
    try {
      await axios.delete(`${API}/inventory/${id}`);
      toast.success("Item gelöscht");
      setDeleteDialog({ open: false, type: null, id: null, name: "" });
      loadInventory();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleStockChange = async (itemId, quantity, isPersonalUse = false) => {
    try {
      await axios.post(`${API}/inventory/stock`, { 
        item_id: itemId, 
        quantity,
        is_personal_use: isPersonalUse
      });
      toast.success("Bestand aktualisiert");
      loadInventory();
      loadProtocol();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  // Stock Dialog Handlers
  const addItemToEinlagern = () => {
    if (!selectedItemId || !stockQuantity || stockQuantity <= 0) {
      toast.error("Bitte Item und Menge auswählen");
      return;
    }

    const item = inventory.find(i => i.id === selectedItemId);
    if (!item) return;

    // Add item with 'add' type
    setStockItems([...stockItems, {
      id: item.id,
      name: item.name,
      quantity: parseInt(stockQuantity),
      type: 'add', // Einlagern
      isPersonalUse: false
    }]);

    // Reset form
    setSelectedItemId("");
    setStockQuantity("");
    setStockPersonalUse(false);
  };

  const addItemToEntnehmen = () => {
    if (!selectedItemId || !stockQuantity || stockQuantity <= 0) {
      toast.error("Bitte Item und Menge auswählen");
      return;
    }

    const item = inventory.find(i => i.id === selectedItemId);
    if (!item) return;

    // Add item with 'remove' type
    setStockItems([...stockItems, {
      id: item.id,
      name: item.name,
      quantity: parseInt(stockQuantity),
      type: 'remove', // Entnehmen
      isPersonalUse: stockPersonalUse
    }]);

    // Reset form
    setSelectedItemId("");
    setStockQuantity("");
    setStockPersonalUse(false);
  };

  const removeStockItem = (index) => {
    setStockItems(stockItems.filter((_, i) => i !== index));
  };

  const handleFertig = async () => {
    if (stockItems.length === 0) {
      toast.error("Bitte fügen Sie mindestens ein Item hinzu");
      return;
    }

    try {
      // Prepare batch data
      const batchItems = stockItems.map(item => ({
        item_id: item.id,
        quantity: item.type === 'add' ? item.quantity : -item.quantity,
        is_personal_use: item.isPersonalUse,
        is_sale: false
      }));

      // Send as batch to create grouped protocol entry
      await axios.post(`${API}/inventory/stock/batch`, batchItems);
      
      toast.success("Bestand erfolgreich aktualisiert");
      setShowStockDialog(false);
      setStockItems([]);
      loadInventory();
      loadProtocol();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!isHunterEditor) {
      toast.error("Keine Berechtigung zum Löschen");
      return;
    }
    try {
      await axios.delete(`${API}/protocol/${logId}`);
      toast.success("Eintrag gelöscht");
      setDeleteDialog({ open: false, type: null, id: null, name: "" });
      loadProtocol();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleDeleteOrderProtocol = async (orderId) => {
    try {
      await axios.delete(`${API}/protocol/order/${orderId}`);
      toast.success("Bestellung gelöscht und Inventar wiederhergestellt");
      setDeleteDialog({ open: false, type: null, id: null, name: "" });
      loadProtocol();
      loadInventory(); // Reload inventory to show restored stock
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleDeleteBatchProtocol = async (batchId) => {
    try {
      await axios.delete(`${API}/protocol/batch/${batchId}`);
      toast.success("Sammelaktion gelöscht und Inventar wiederhergestellt");
      setDeleteDialog({ open: false, type: null, id: null, name: "" });
      loadProtocol();
      loadInventory(); // Reload inventory to show restored stock
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const openDeleteDialog = (type, id, name) => {
    setDeleteDialog({ open: true, type, id, name });
  };

  const confirmDelete = () => {
    if (deleteDialog.type === 'item') {
      handleDeleteItem(deleteDialog.id);
    } else if (deleteDialog.type === 'log') {
      handleDeleteLog(deleteDialog.id);
    } else if (deleteDialog.type === 'order') {
      handleDeleteOrderProtocol(deleteDialog.id);
    } else if (deleteDialog.type === 'batch') {
      handleDeleteBatchProtocol(deleteDialog.id);
    }
  };

  const crafting = calculateCrafting();

  return (
    <>
      {/* Running Banner - Full Width */}
      <RunningBanner />
      
      <div className="max-w-screen-xl mx-auto p-8">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#f4e8d0' }}>Hunter Bereich</h2>
        <div className="flex flex-wrap gap-2 md:gap-3">
          <Button
            onClick={() => setShowStockDialog(true)}
            className="rdr-button flex-1 md:flex-none text-sm md:text-base"
            style={{ background: '#6d5838', minWidth: '140px' }}
          >
            <ArrowDownToLine size={14} className="mr-1 md:mr-2" />
            <ArrowUpFromLine size={14} className="mr-1 md:mr-2" />
            <span className="hidden sm:inline">Einlagern/Entnehmen</span>
            <span className="sm:hidden">Ein-/Auslagern</span>
          </Button>
          <Button
            onClick={() => navigate('/hunter/orders')}
            className="rdr-button flex-1 md:flex-none text-sm md:text-base"
            style={{ background: '#22c55e', minWidth: '140px' }}
          >
            <ShoppingCart size={14} className="mr-1 md:mr-2" />
            <span className="hidden sm:inline">Zu Bestellungen</span>
            <span className="sm:hidden">Bestellungen</span>
          </Button>
          {isHunterEditor && (
            <Button
              onClick={() => setEditMode(!editMode)}
              className="rdr-button flex-1 md:flex-none text-sm md:text-base"
              style={{ 
                background: editMode ? '#8b7355' : '#3d2f1f',
                color: '#f4e8d0',
                minWidth: '140px'
              }}
            >
              {editMode ? '✓ Aktiv' : 'Bearbeitungsmodus'}
            </Button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_5fr_4fr] gap-6">
        {/* Left Column - Calculators */}
        <div className="space-y-6" style={{ height: 'fit-content' }}>
          {/* Package Calculator */}
          <div className="rdr-card" data-testid="package-calculator">
            <h3 className="text-xl font-bold mb-4" style={{ color: '#3d2f1f' }}>
              <Package className="inline mr-2" size={20} />
              Paket Kalkulator
            </h3>
            <div className="space-y-4">
              <div>
                <Label style={{ color: '#3d2f1f' }}>Anzahl Pakete</Label>
                <Input
                  data-testid="package-quantity-input"
                  type="number"
                  min="1"
                  value={packages}
                  onChange={(e) => setPackages(e.target.value)}
                  className="rdr-input w-full"
                />
              </div>
              <div className="p-3 rounded" style={{ background: 'rgba(139, 115, 85, 0.2)' }}>
                <p className="text-sm" style={{ color: '#3d2f1f' }}>Preis pro Paket:</p>
                <p className="text-2xl font-bold" style={{ color: '#6d5838' }}>
                  ${parseInt(packages) >= 10 ? '35' : parseInt(packages) >= 5 ? '37' : '40'}
                </p>
              </div>
              <div className="p-4 rounded" style={{ background: 'rgba(139, 115, 85, 0.3)' }}>
                <p className="text-sm" style={{ color: '#3d2f1f' }}>Gesamtpreis:</p>
                <p className="text-3xl font-bold" data-testid="total-price" style={{ color: '#3d2f1f' }}>
                  ${calculatePackagePrice()}
                </p>
              </div>
            </div>
          </div>

          {/* Crafting Calculator */}
          <div className="rdr-card" data-testid="crafting-calculator">
            <h3 className="text-xl font-bold mb-4" style={{ color: '#3d2f1f' }}>Crafting Rechner</h3>
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded" style={{ background: 'rgba(139, 115, 85, 0.15)' }}>
                <p style={{ color: '#3d2f1f', fontWeight: 600 }}>Benötigt:</p>
                <p style={{ color: '#6d5838' }}>• {crafting.bratwurst}x Bratwurst</p>
                <p style={{ color: '#6d5838' }}>• {crafting.dosenwurst}x Dosenwurst</p>
              </div>
              <div className="p-3 rounded" style={{ background: 'rgba(139, 115, 85, 0.15)' }}>
                <p style={{ color: '#3d2f1f', fontWeight: 600 }}>Crafting benötigt:</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.bratwurstCrafts}x Bratwurst craften</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.dosenwurstCrafts}x Dosenwurst craften</p>
              </div>
              <div className="p-3 rounded" style={{ background: 'rgba(139, 115, 85, 0.15)' }}>
                <p style={{ color: '#3d2f1f', fontWeight: 600 }}>Materialien:</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.tierdarm}x Tierdarm</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.schweinefleisch}x Schweinefleisch</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.dose}x Dose</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.fett}x Fett</p>
                <p style={{ color: '#6d5838' }}>• {crafting.materials.wildfleisch}x Wildfleisch</p>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column - Inventory */}
        <div style={{ height: 'fit-content' }}>
          <div className="rdr-card" style={{ height: '100%' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ color: '#3d2f1f' }}>Bestandsliste</h3>
              {(isHunterEditor && editMode) && (
                <Button
                  data-testid="add-item-button"
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="rdr-button"
                >
                  <Plus size={16} className="mr-1" />
                  Hinzufügen
                </Button>
              )}
            </div>

            {showAddForm && (
              <div className="mb-4 p-4 rounded" style={{ background: 'rgba(139, 115, 85, 0.15)' }}>
                <h4 className="font-bold mb-3" style={{ color: '#3d2f1f' }}>Neues Item hinzufügen</h4>
                <div className="space-y-3">
                  <div>
                    <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Name</Label>
                    <Input
                      data-testid="new-item-name"
                      placeholder="z.B. Wildfleisch"
                      value={newItem.name}
                      onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                      className="rdr-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Preis ($)</Label>
                    <Input
                      data-testid="new-item-price"
                      type="number"
                      step="0.01"
                      placeholder="z.B. 15.50"
                      value={newItem.price}
                      onChange={(e) => setNewItem({...newItem, price: parseFloat(e.target.value)})}
                      className="rdr-input"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Anfangsbestand (Menge)</Label>
                    <Input
                      data-testid="new-item-stock"
                      type="number"
                      placeholder="z.B. 100"
                      value={newItem.stock}
                      onChange={(e) => setNewItem({...newItem, stock: parseInt(e.target.value)})}
                      className="rdr-input"
                    />
                  </div>
                  <Button data-testid="save-new-item" onClick={handleAddItem} className="rdr-button w-full">Speichern</Button>
                </div>
              </div>
            )}

            <div className="space-y-3 scroll-container" style={{ maxHeight: '706px', overflowY: 'auto' }}>
              {inventory.sort((a, b) => a.name.localeCompare(b.name)).map((item) => (
                <div key={item.id} className="p-4 rounded" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
                  {editingItem?.id === item.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                        className="rdr-input"
                      />
                      <Input
                        type="number"
                        value={editingItem.price}
                        onChange={(e) => setEditingItem({...editingItem, price: parseFloat(e.target.value)})}
                        className="rdr-input"
                      />
                      <Input
                        type="number"
                        value={editingItem.stock}
                        onChange={(e) => setEditingItem({...editingItem, stock: parseInt(e.target.value)})}
                        className="rdr-input"
                      />
                      <div className="flex gap-2">
                        <Button onClick={handleUpdateItem} className="rdr-button flex-1">Speichern</Button>
                        <Button onClick={() => setEditingItem(null)} className="rdr-button flex-1">Abbrechen</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-bold text-lg" style={{ color: '#3d2f1f' }}>{item.name}</h4>
                          <p className="text-sm" style={{ color: '#6d5838' }}>${item.price}</p>
                          <p className="text-xl font-bold mt-1" style={{ color: '#3d2f1f' }}>
                            Bestand: <span style={{ color: '#6d5838' }}>{item.stock}</span>
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {(isHunterEditor && editMode) && (
                            <>
                              <button
                                data-testid={`edit-item-${item.id}`}
                                onClick={() => setEditingItem(item)}
                                className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                                title="Item bearbeiten"
                              >
                                <Edit2 size={16} style={{ color: '#3d2f1f' }} />
                              </button>
                              <button
                                data-testid={`delete-item-${item.id}`}
                                onClick={() => openDeleteDialog('item', item.id, item.name)}
                                className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                                title="Item löschen"
                              >
                                <Trash2 size={16} style={{ color: '#8b4513' }} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

 
        {/* Right Column - Protocol */}
        <div style={{ height: 'fit-content' }}>
          <div className="rdr-card" style={{ height: '100%' }}>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#3d2f1f' }}>Protokoll</h3>
            <div className="space-y-2 scroll-container" style={{ maxHeight: '706px', overflowY: 'auto' }}>
              {getGroupedProtocol().map((entry) => {
                if (entry.type === 'order') {
                  // Render order as grouped entry
                  return (
                    <div key={entry.id} className="p-3 rounded text-sm" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-bold" style={{ color: '#3d2f1f' }}>{entry.display_name}</p>
                          <p style={{ color: '#6d5838', fontWeight: 'bold' }}>
                            📦 Bestellung: {entry.order_customer}
                          </p>
                          <div className="mt-1 space-y-0.5">
                            {entry.items.map((item, idx) => (
                              <p key={idx} className="text-xs" style={{ color: '#6d5838' }}>
                                • {item.quantity}x {item.item_name}
                              </p>
                            ))}
                          </div>
                          <p>
                            <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#22c55e', color: '#fff' }}>💰 Verkauf</span>
                          </p>
                          <p className="text-xs mt-1" style={{ color: '#8b7355' }}>
                            {new Date(entry.timestamp).toLocaleString('de-DE', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        {(isHunterEditor && editMode) && (
                          <button
                            data-testid={`delete-order-${entry.order_id}`}
                            onClick={() => openDeleteDialog('order', entry.order_id, `Bestellung: ${entry.order_customer}`)}
                            className="p-1 rounded hover:bg-[#8b7355] hover:bg-opacity-30 ml-2"
                            title="Bestellung löschen"
                          >
                            <Trash2 size={14} style={{ color: '#8b4513' }} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                } else if (entry.type === 'batch') {
                  // Render batch as grouped entry
                  return (
                    <div key={entry.id} className="p-3 rounded text-sm" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-bold" style={{ color: '#3d2f1f' }}>{entry.display_name}</p>
                          <p style={{ color: '#6d5838', fontWeight: 'bold' }}>
                            📦 Bestandsaktualisierung
                          </p>
                          <div className="mt-1 space-y-0.5">
                            {entry.items.map((item, idx) => (
                              <p key={idx} className="text-xs" style={{ color: '#6d5838' }}>
                                <span style={{ color: item.action === 'added' ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                                  {item.action === 'added' ? '+' : '-'}
                                </span> {item.quantity}x {item.item_name}
                                {item.is_personal_use && <span className="ml-1" style={{ color: '#8b7355' }}>| 🏠 Eigenbedarf</span>}
                              </p>
                            ))}
                          </div>
                          <p className="text-xs mt-1" style={{ color: '#8b7355' }}>
                            {new Date(entry.timestamp).toLocaleString('de-DE', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        {(isHunterEditor && editMode) && (
                          <button
                            data-testid={`delete-batch-${entry.batch_id}`}
                            onClick={() => openDeleteDialog('batch', entry.batch_id, 'Bestandsaktualisierung')}
                            className="p-1 rounded hover:bg-[#8b7355] hover:bg-opacity-30 ml-2"
                            title="Bestandsaktualisierung löschen"
                          >
                            <Trash2 size={14} style={{ color: '#8b4513' }} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  // Render regular log entry
                  const log = entry;
                  return (
                    <div key={log.id} className="p-3 rounded text-sm flex justify-between items-start" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
                      <div className="flex-1">
                        <p className="font-bold" style={{ color: '#3d2f1f' }}>{log.display_name || log.username}</p>
                        <p style={{ color: '#6d5838' }}>
                          <span style={{ color: log.action === 'added' ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                            {log.action === 'added' ? '+' : '-'}
                          </span> {log.quantity}x {log.item_name}
                        </p>
                        {log.is_personal_use && (
                          <p>
                            <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#8b7355', color: '#f4e8d0' }}>🏠 Eigenbedarf</span>
                          </p>
                        )}
                        {log.is_sale === true && (
                          <p>
                            <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#22c55e', color: '#fff' }}>💰 Verkauf</span>
                          </p>
                        )}
                        <p className="text-xs" style={{ color: '#8b7355' }}>
                          {new Date(log.timestamp).toLocaleString('de-DE', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                      {(isHunterEditor && editMode) && (
                        <button
                          data-testid={`delete-log-${log.id}`}
                          onClick={() => openDeleteDialog('log', log.id, log.item_name)}
                          className="p-1 rounded hover:bg-[#8b7355] hover:bg-opacity-30 ml-2"
                          title="Eintrag löschen"
                        >
                          <Trash2 size={14} style={{ color: '#8b4513' }} />
                        </button>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null, name: "" })}>
        <AlertDialogContent className="rdr-card border-4 border-[#8b7355]" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '500px' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold" style={{ color: '#3d2f1f', fontFamily: 'Crimson Text, serif' }}>
              {deleteDialog.type === 'item' ? 'Item löschen?' : deleteDialog.type === 'order' ? 'Bestellung löschen?' : deleteDialog.type === 'batch' ? 'Bestandsaktualisierung löschen?' : 'Protokoll-Eintrag löschen?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base" style={{ color: '#6d5838' }}>
              {deleteDialog.type === 'item' 
                ? `Möchten Sie "${deleteDialog.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
                : deleteDialog.type === 'order'
                ? `Möchten Sie "${deleteDialog.name}" wirklich löschen? Alle Protokolleinträge dieser Bestellung werden gelöscht und das Inventar wird wiederhergestellt.`
                : deleteDialog.type === 'batch'
                ? `Möchten Sie "${deleteDialog.name}" wirklich löschen? Alle Protokolleinträge dieser Bestandsaktualisierung werden gelöscht und das Inventar wird wiederhergestellt.`
                : `Möchten Sie den Protokoll-Eintrag für "${deleteDialog.name}" wirklich löschen?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="rdr-button" style={{ background: 'linear-gradient(to bottom, #6d5838 0%, #5a4a3a 100%)' }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="rdr-button" 
              style={{ background: 'linear-gradient(to bottom, #8b4513 0%, #6b3410 100%)' }}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stock Management Dialog */}
      <Dialog open={showStockDialog} onOpenChange={setShowStockDialog}>
        <DialogContent 
          className="rdr-card border-4 border-[#8b7355] max-w-2xl" 
          style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: '#3d2f1f', fontFamily: 'Crimson Text, serif' }}>
              Bestand Einlagern/Entnehmen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Item Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Item auswählen</Label>
                <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                  <SelectTrigger className="rdr-input">
                    <SelectValue placeholder="Item wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {inventory.sort((a, b) => a.name.localeCompare(b.name)).map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} (Bestand: {item.stock})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Menge</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="z.B. 10"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="rdr-input"
                />
              </div>
            </div>

            {/* Personal Use Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="stock-personal-use"
                checked={stockPersonalUse}
                onChange={(e) => setStockPersonalUse(e.target.checked)}
                className="w-4 h-4 rounded border-2 border-[#8b7355] cursor-pointer"
                style={{ accentColor: '#8b7355' }}
              />
              <label 
                htmlFor="stock-personal-use" 
                className="text-sm cursor-pointer select-none"
                style={{ color: '#6d5838' }}
              >
                Als Eigenbedarf markieren (nur für Entnahme)
              </label>
            </div>

            {/* Add to List Buttons */}
            <div className="flex gap-3">
              <Button 
                onClick={addItemToEinlagern}
                className="rdr-button flex-1"
                style={{ background: '#22c55e' }}
              >
                <ArrowDownToLine size={16} className="mr-2" />
                Einlagern
              </Button>
              <Button 
                onClick={addItemToEntnehmen}
                className="rdr-button flex-1"
                style={{ background: '#ef4444' }}
              >
                <ArrowUpFromLine size={16} className="mr-2" />
                Entnehmen
              </Button>
            </div>

            {/* Items List */}
            {stockItems.length > 0 && (
              <div className="border-2 border-[#8b7355] rounded p-3" style={{ background: 'rgba(244, 232, 208, 0.3)' }}>
                <h4 className="font-bold mb-2" style={{ color: '#3d2f1f' }}>Items in der Liste:</h4>
                <div className="space-y-2">
                  {stockItems.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-2 rounded" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
                      <div className="flex-1">
                        <span className={`font-semibold mr-2 ${item.type === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                          {item.type === 'add' ? '+ ' : '- '}
                        </span>
                        <span className="font-semibold" style={{ color: '#3d2f1f' }}>{item.name}</span>
                        <span className="ml-2" style={{ color: '#6d5838' }}>× {item.quantity}</span>
                        {item.isPersonalUse && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded" style={{ background: '#8b7355', color: '#f4e8d0' }}>
                            🏠 Eigenbedarf
                          </span>
                        )}
                        <span className="ml-2 text-xs px-2 py-0.5 rounded" style={{ 
                          background: item.type === 'add' ? '#22c55e' : '#ef4444', 
                          color: '#fff' 
                        }}>
                          {item.type === 'add' ? '📥 Einlagern' : '📤 Entnehmen'}
                        </span>
                      </div>
                      <button
                        onClick={() => removeStockItem(index)}
                        className="p-1 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                        title="Aus Liste entfernen"
                      >
                        <Trash2 size={14} style={{ color: '#8b4513' }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fertig Button */}
            {stockItems.length > 0 && (
              <Button 
                onClick={handleFertig}
                className="rdr-button w-full"
                style={{ background: '#8b7355' }}
              >
                ✓ Fertig - Alle Änderungen übernehmen
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}